import type { PDFDocumentProxy } from "pdfjs-dist";
import * as pdfjs from "pdfjs-dist";
import React, { FC, ReactNode, useEffect, useRef, useState } from "react";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url,
).toString();

/** Draws one page into a canvas sized to the page's box on screen. */
const PageCanvas: FC<{
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
}> = ({ pdf, pageNumber, width }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let cancelled = false;
    let task: { cancel: () => void } | null = null;
    pdf.getPage(pageNumber).then((page) => {
      if (cancelled || !canvas.current) return;
      const base = page.getViewport({ scale: 1 });
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({
        scale: (width / base.width) * ratio,
      });
      const el = canvas.current;
      el.width = Math.floor(viewport.width);
      el.height = Math.floor(viewport.height);
      const ctx = el.getContext("2d");
      if (!ctx) return;
      const render = page.render({ canvasContext: ctx, viewport });
      task = render;
      render.promise.catch(() => undefined);
    });
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [pdf, pageNumber, width]);
  return <canvas ref={canvas} className={"absolute inset-0 h-full w-full"} />;
};

/**
 * Shows every page of a PDF, one under the other, and lets the caller draw
 * fields over each page (positions are fractions of the page, 0..1).
 */
export const PdfPages: FC<{
  load: () => Promise<ArrayBuffer>;
  /** Re-load when this changes. */
  loadKey: string;
  maxWidth?: number;
  overlay?: (page: number) => ReactNode;
  onPageClick?: (page: number, x: number, y: number) => void;
  onError?: (error: Error) => void;
  pageRef?: (page: number, el: HTMLDivElement | null) => void;
}> = ({
  load,
  loadKey,
  maxWidth = 760,
  overlay,
  onPageClick,
  onError,
  pageRef,
}) => {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [sizes, setSizes] = useState<{ w: number; h: number }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(maxWidth);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let doc: PDFDocumentProxy | null = null;
    setPdf(null);
    setError(null);
    // The PDF reader's helper script sometimes fails to start on the first
    // try; open again with a fresh copy (the bytes are handed over each time).
    const open = async (
      bytes: ArrayBuffer,
      tries: number,
    ): Promise<PDFDocumentProxy> => {
      try {
        return await pdfjs.getDocument({ data: new Uint8Array(bytes.slice(0)) })
          .promise;
      } catch (e) {
        if (
          tries <= 1 ||
          cancelled ||
          !/worker/i.test((e as Error).message ?? "")
        )
          throw e;
        await new Promise((r) => setTimeout(r, 700));
        return open(bytes, tries - 1);
      }
    };
    load()
      .then(async (bytes) => {
        try {
          return await open(bytes, 3);
        } catch (e) {
          throw new Error(
            "The document couldn't be opened. Please refresh the page and try again.",
          );
        }
      })
      .then(async (loaded) => {
        doc = loaded;
        const list: { w: number; h: number }[] = [];
        for (let i = 1; i <= loaded.numPages; i++) {
          const v = (await loaded.getPage(i)).getViewport({ scale: 1 });
          list.push({ w: v.width, h: v.height });
        }
        if (cancelled) return;
        setSizes(list);
        setPdf(loaded);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e.message || "The document couldn't be opened.");
        onError?.(e);
      });
    return () => {
      cancelled = true;
      doc?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () =>
      setWidth(Math.min(maxWidth, Math.max(240, el.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [maxWidth]);

  return (
    <div ref={box} className={"flex w-full flex-col items-center gap-5"}>
      {error && (
        <div
          className={
            "rounded-[10px] border border-[#F3C5C5] bg-[#FDF1F1] px-4 py-3 text-sm font-semibold text-[#B42318]"
          }>
          {error}
        </div>
      )}
      {!pdf && !error && (
        <div className={"py-16 text-sm font-semibold text-[#6B7185]"}>
          Opening the document…
        </div>
      )}
      {pdf &&
        sizes.map((size, i) => (
          <div
            key={i}
            ref={(el) => pageRef?.(i + 1, el)}
            data-page={i + 1}
            className={
              "relative select-none bg-white shadow-[0_2px_10px_rgba(31,36,51,0.12)]"
            }
            style={{ width, height: (width * size.h) / size.w }}
            onClick={(e) => {
              if (
                !onPageClick ||
                e.target !== e.currentTarget.querySelector("canvas")
              )
                return;
              const rect = e.currentTarget.getBoundingClientRect();
              onPageClick(
                i + 1,
                (e.clientX - rect.left) / rect.width,
                (e.clientY - rect.top) / rect.height,
              );
            }}>
            <PageCanvas pdf={pdf} pageNumber={i + 1} width={width} />
            {overlay?.(i + 1)}
          </div>
        ))}
    </div>
  );
};
